import { useState, useEffect, useCallback } from "react";
import {
  Button, Card, Box, Typography, Chip, Fade,
  Alert, Tooltip, Paper, Grid, TextField, Container, LinearProgress,
  IconButton, Dialog, DialogTitle, DialogContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { styled, keyframes, alpha } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import ViewJobPlan from "./ViewJobPlan";
import NotStartedOutlinedIcon from "@mui/icons-material/NotStartedOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CreateIcon from "@mui/icons-material/Create";
import SearchIcon from "@mui/icons-material/Search";
import WorkIcon from "@mui/icons-material/Work";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import "./ShiftDetails.css";
import ShiftRemarksButton from "./shiftRemarksButton";
import { useEmployee } from "./EmployeeContext";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Animations (keep generic; colors come from theme in sx/styles)
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;
const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
`;
const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

// Styled components (theme-aware)
const MainContainer = styled(Container)(({ theme }) => ({
  height: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(1),
  position: "relative",
  overflow: "hidden",
  background: theme.palette.background.default
}));

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  width: "100%",
  maxWidth: "900px",
  maxHeight: "90vh",
  overflowY: "auto",
  background: alpha(theme.palette.background.paper, 0.97),
  backdropFilter: "blur(12px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[10],
  animation: `${fadeInUp} 0.5s ease-out`,
  position: "relative",
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-thumb": {
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: 4
  }
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(1.5),
  position: "relative"
}));

const TitleText = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  fontWeight: 800,
  fontSize: "2rem",
  marginBottom: theme.spacing(0.5),
  position: "relative",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -6,
    left: "50%",
    transform: "translateX(-50%)",
    width: 50,
    height: 3,
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: 2
  }
}));


const ActionButton = styled(Button)(({ theme, ownerState }) => ({
  borderRadius: theme.spacing(1.8),
  padding: theme.spacing(1, 2.5),
  fontWeight: 700,
  textTransform: "none",
  fontSize: "0.95rem",
  minHeight: 42,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  position: "relative",
  overflow: "hidden",
  "&:hover": {
    transform: "translateY(-3px)",
    boxShadow:
      ownerState?.variant === "contained"
        ? `0 8px 20px ${alpha(theme.palette.primary.main, 0.4)}`
        : `0 6px 15px ${alpha(theme.palette.common.black, 0.15)}`
  },
  "&:active": { transform: "translateY(-1px)" },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-100%",
    width: "100%",
    height: "100%",
    background:
      ownerState?.variant === "contained"
        ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)"
        : `linear-gradient(90deg, transparent, ${alpha(theme.palette.primary.main, 0.1)}, transparent)`,
    transition: "left 0.5s"
  },
  "&:hover::before": { left: "100%" }
}));

const SelectedJobCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.8),
  borderRadius: theme.spacing(1.8),
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: theme.palette.common.white,
  border: `2px solid ${alpha(theme.palette.common.white, 0.3)}`,
  boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  animation: `${slideInRight} 0.5s ease-out`,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 8px 30px ${alpha(theme.palette.primary.main, 0.4)}`
  }
}));

const StatsChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.3),
  fontWeight: 600,
  borderRadius: theme.spacing(1.2),
  background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(
    theme.palette.secondary.main,
    0.08
  )})`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
  fontSize: "0.8rem",
  transition: "all 0.3s ease",
  "&:hover": {
    background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.18)}, ${alpha(
      theme.palette.secondary.main,
      0.18
    )})`,
    transform: "scale(1.05)"
  }
}));

function adjustAndFormatDate(dateString) {
  if (!dateString) {
    return "Not Set";
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return "Not Set";
  }
  return new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true
  }).format(date);
}

function JobProcessSelection() {
  const location = useLocation();
  const [username] = useState(location.state?.username || sessionStorage.getItem("username") || "");
  const navigate = useNavigate();
  const [machineCode, setMachineCode] = useState("");
  const [categorizedJobs, setCategorizedJobs] = useState([]);
  const [selectedJobData, setSelectedJobData] = useState(null);
  const [isStartJobButtonDisabled, setIsStartJobButtonDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showJobDetails, setShowJobDetails] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const { employeeName } = useEmployee();
  const [searchMode, setSearchMode] = useState("plan");
  const [openDialog, setOpenDialog] = useState(false);
  const [filteredJobs, setFilteredJobs] = useState([]);

  const fetchLineupJobs = async () => {
    if (!machineCode) return;
    try {
      const response = await axios.get(`${API_BASE_URL}/fetchLineupJobsForGroup`, { params: { machineCode } });
      const categorized = response.data.map((job) => ({
        label: `${adjustAndFormatDate(job.opstartDate)} - ${adjustAndFormatDate(job.opendDate)}`,
        job
      }));
      categorized.sort((a, b) => a.label.localeCompare(b.label));
      setCategorizedJobs(categorized);
      setIsLoading(false);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch lineup jobs:", err);
      setError("Failed to fetch lineup jobs. Please try again.");
      setIsLoading(false);
    }
  };

  const handleSearchFromPlan = () => {
    setSearchMode("plan");
    setSearchValue("");
    setShowJobDetails(false);
    fetchPlannedJobs(machineCode, new Date());
    setOpenDialog(true);
  };

  const handleSearchFromLineup = () => {
    setSearchMode("lineup");
    setSearchValue("");
    setShowJobDetails(false);
    fetchLineupJobs();
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSearchValue("");
  };

  // Filter jobs based on search value
  useEffect(() => {
    if (!searchValue.trim()) {
      setFilteredJobs(categorizedJobs);
    } else {
      const searchLower = searchValue.toLowerCase();
      const filtered = categorizedJobs.filter((item) => {
        const job = item.job;
        return (
          job.jobNumber?.toLowerCase().includes(searchLower) ||
          job.customer?.toLowerCase().includes(searchLower) ||
          job.OpDesctiption?.toLowerCase().includes(searchLower) ||
          job.WOdescription?.toLowerCase().includes(searchLower) ||
          (searchMode === "plan" && job.jobSequenceNumber?.toString().includes(searchLower))
        );
      });
      setFilteredJobs(filtered);
    }
  }, [searchValue, categorizedJobs, searchMode]);

  const Buttonclickmaincategories = () =>
    navigate("/MainCategories", { state: { username } });

  const handleclicktoManual = () =>
    navigate("/ManualEnter", { state: { username, employeeName } });

  const categorizeJobs = useCallback((jobs) => {
    const categorized = jobs.map((job) => ({
      label: `${adjustAndFormatDate(job.planningFrom)} - ${adjustAndFormatDate(job.planingTo)}`,
      job
    }));
    categorized.sort((a, b) => a.label.localeCompare(b.label));
    setCategorizedJobs(categorized);
    setIsLoading(false);
  }, []);

  const fetchPlannedJobs = useCallback(
    async (machineCode, selectedDateTime) => {
      if (!machineCode) return;
      if (!(selectedDateTime instanceof Date && !isNaN(selectedDateTime))) {
        console.error("Invalid date:", selectedDateTime);
        setError("Invalid date provided");
        setIsLoading(false);
        return;
      }
      const adjustedDateTime = new Date(
        selectedDateTime.getTime() + 5 * 60 * 60 * 1000 + 30 * 60 * 1000
      );
      const formattedDateTime = adjustedDateTime.toISOString().slice(0, 19).replace("T", " ");
      try {
        const response = await axios.get(`${API_BASE_URL}/OperatorViewPlan`, {
          params: { machineCode, dateTime: formattedDateTime }
        });
        categorizeJobs(response.data);
        setError(null);
      } catch (error) {
        console.error("Error fetching planned jobs:", error);
        setError("Failed to fetch planned jobs. Please try again.");
        setIsLoading(false);
      }
    },
    [categorizeJobs]
  );

  useEffect(() => {
    let redirectTimer;

    const fetchMachineCode = async () => {
      setIsLoading(true);

      // Guard: if username is missing (direct URL hit / session lost),
      // bounce straight to login.
      if (!username) {
        navigate("/", { replace: true });
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/getMachineCode?username=${username}`
        );

        // Backend returned 200 but no machine assigned — treat as failure.
        if (!response.data || !response.data.machineCode) {
          throw new Error("No workstation assigned to this user.");
        }

        setMachineCode(response.data.machineCode);
        setError(null);
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching machine code:", err);

        // Friendly message based on error type
        const status = err?.response?.status;
        let message =
          "Failed to fetch workstation information. Redirecting to login...";

        if (status === 401 || status === 403) {
          message = "Session expired. Redirecting to login...";
        } else if (status === 404) {
          message = "Workstation not found for this user. Redirecting to login...";
        } else if (err?.message === "Network Error") {
          message = "Network error. Redirecting to login...";
        }

        setError(message);
        setIsLoading(false);

        // Session data is intentionally NOT cleared — the login page's
        // alterUserLogin flow uses localStorage.username to resume sessions.

        // Wait briefly so the user sees the message, then redirect.
        redirectTimer = setTimeout(() => {
          navigate("/", { replace: true });
        }, 2500);
      }
    };

    fetchMachineCode();

    return () => {
      if (redirectTimer) clearTimeout(redirectTimer);
    };
  }, [username, navigate]);

  const handleStartJob = async () => {
    if (!selectedJobData) return;
    setIsStartJobButtonDisabled(true);
    const {
      jobNumber,
      customer,
      OpDesctiption,
      WOdescription,
      plannedQty,
      OperationalTime,
      SetupAndOpeTime,
      SetupTime
    } = selectedJobData;
    const currentDate = new Date();
    currentDate.setHours(currentDate.getHours() - 5);
    currentDate.setMinutes(currentDate.getMinutes() - 30);
    const startedTime = currentDate.toISOString();
    try {
      const response = await axios.post(`${API_BASE_URL}/Jobstart`, {
        jobNumber,
        customer,
        operation: OpDesctiption,
        product: WOdescription,
        username,
        machineCode,
        startedTime,
        employeeName
      });
      const jobStartedDate = new Date(response.data.JobStartedTime);
      jobStartedDate.setHours(jobStartedDate.getHours() - 5);
      jobStartedDate.setMinutes(jobStartedDate.getMinutes() - 30);
      navigate("/JobOperation", {
        state: {
          jobNumber,
          customer,
          operation: OpDesctiption,
          product: WOdescription,
          startedTime: jobStartedDate.toISOString(),
          username,
          plannedQty,
          OperationalTime,
          SetupAndOpeTime,
          SetupTime,
          machineCode
        }
      });
    } catch (error) {
      console.error("Error inserting operation log:", error);
      setIsStartJobButtonDisabled(false);
      setError("Failed to start job. Please try again.");
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      window.history.pushState(null, document.title, window.location.href);
      alert("You can't go back from this window-කණගාටුයි! ඔබට මෙම පේජ් එකෙන් පසුපසට යා නොහැක.");
    };
    window.addEventListener("popstate", handlePopState);
    window.history.pushState(null, document.title, window.location.href);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [navigate]);


  return (
    <MainContainer maxWidth={false}>
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        fullWidth
        maxWidth={false}
        scroll="paper"
        PaperProps={{
          sx: {
            width: "95vw",
            height: "95vh",
            maxHeight: "95vh",
            borderRadius: 3,
            backgroundColor: (t) => alpha(t.palette.background.paper, 0.98),
            border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "1.2rem",
            color: (t) => t.palette.primary.main,
            pb: 1,
            borderBottom: (t) => `1px solid ${alpha(t.palette.divider, 0.7)}`,
            background: (t) => alpha(t.palette.primary.main, 0.04)
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {/* Refresh Button */}
              <IconButton
                onClick={() => {
                  if (searchMode === "plan") fetchPlannedJobs(machineCode, new Date());
                  else fetchLineupJobs();
                }}
                sx={{
                  backgroundColor: (t) => alpha(t.palette.primary.main, 0.08),
                  color: (t) => t.palette.primary.main,
                  transition: "all 0.3s",
                  '&:hover': {
                    backgroundColor: (t) => alpha(t.palette.primary.main, 0.2),
                    transform: "scale(1.1)"
                  }
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {searchMode === "plan" ? (
                  <WorkIcon sx={{ fontSize: "1.4rem", color: (t) => t.palette.primary.main }} />
                ) : (
                  <PrecisionManufacturingIcon sx={{ fontSize: "1.4rem", color: (t) => t.palette.success.main }} />
                )}
                <Typography variant="h6" sx={{ fontWeight: 700, color: (t) => t.palette.primary.main }}>
                  {searchMode === "plan" ? "Planned Job Search" : "Lineup Job Search"}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip
                  label={
                    isLoading ? "Loading..." : `${categorizedJobs.length} ${categorizedJobs.length === 1 ? "Job" : "Jobs"} Available`
                  }
                  icon={!isLoading ? <WorkIcon sx={{ fontSize: "0.9rem !important" }} /> : null}
                  size="medium"
                  sx={{
                    background: (t) =>
                      isLoading
                        ? `linear-gradient(45deg, ${alpha(t.palette.text.disabled, 0.1)}, ${alpha(
                          t.palette.text.disabled,
                          0.1
                        )})`
                        : `linear-gradient(45deg, ${alpha(t.palette.primary.main, 0.1)}, ${alpha(
                          t.palette.secondary.main,
                          0.1
                        )})`,
                    border: (t) =>
                      isLoading
                        ? `1px solid ${alpha(t.palette.text.disabled, 0.2)}`
                        : `1px solid ${alpha(t.palette.primary.main, 0.3)}`,
                    color: (t) => (isLoading ? t.palette.text.disabled : t.palette.primary.main),
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    "& .MuiChip-icon": {
                      color: (t) => (isLoading ? t.palette.text.disabled : t.palette.primary.main)
                    },
                    animation: isLoading ? `${shimmer} 1.5s infinite` : "none",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                      boxShadow: (t) => `0 4px 12px ${alpha(t.palette.primary.main, 0.2)}`
                    }
                  }}
                />

                {machineCode && (
                  <Chip
                    label={`Machine: ${machineCode}`}
                    icon={<PrecisionManufacturingIcon sx={{ fontSize: "0.9rem !important" }} />}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: (t) => alpha(t.palette.primary.main, 0.3),
                      color: (t) => t.palette.primary.main,
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      "& .MuiChip-icon": { color: (t) => t.palette.primary.main }
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          <IconButton
            aria-label="close"
            onClick={handleDialogClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              backgroundColor: (t) => alpha(t.palette.error.main, 0.1),
              color: (t) => t.palette.error.main,
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: (t) => alpha(t.palette.error.main, 0.2),
                transform: "scale(1.1)"
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            overflowY: "auto",
            maxHeight: "calc(95vh - 60px)",
            p: 0
          }}
        >
          {/* Search Box at the top */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              background: (t) => t.palette.background.paper,
              borderBottom: (t) => `1px solid ${alpha(t.palette.divider, 0.7)}`,
              p: 1
            }}
          >
            <TextField
              fullWidth
              placeholder={
                searchMode === "plan"
                  ? "Search by Job Number, Customer, Operation, or Product..."
                  : "Search by Job Number..."
              }
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              variant="outlined"
              autoFocus
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: (t) => t.palette.primary.main, fontSize: "1.2rem" }} />,
                endAdornment: searchValue && (
                  <IconButton
                    size="small"
                    onClick={() => setSearchValue("")}
                    sx={{
                      color: (t) => t.palette.text.secondary,
                      "&:hover": { backgroundColor: (t) => alpha(t.palette.primary.main, 0.1) }
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: (t) => alpha(t.palette.background.default, 0.5),
                  borderRadius: 2,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: (t) => t.palette.background.default,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: (t) => alpha(t.palette.primary.main, 0.5)
                    }
                  },
                  "&.Mui-focused": {
                    backgroundColor: (t) => t.palette.background.default,
                    boxShadow: (t) => `0 0 0 3px ${alpha(t.palette.primary.main, 0.1)}`
                  }
                }
              }}
            />
          </Box>

          {/* Job List */}
          <Box sx={{ p: 1 }}>
            {isLoading ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <LinearProgress
                  sx={{
                    mb: 2,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: (t) => alpha(t.palette.primary.main, 0.1),
                    "& .MuiLinearProgress-bar": {
                      background: (t) => `linear-gradient(45deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
                      borderRadius: 3
                    }
                  }}
                />
                <Typography variant="body1" color="text.secondary">
                  Loading jobs...
                </Typography>
              </Box>
            ) : filteredJobs.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 6 }}>
                <WorkIcon sx={{ fontSize: 64, color: (t) => alpha(t.palette.primary.main, 0.3), mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {searchValue ? "No jobs found matching your search" : "No jobs available"}
                </Typography>
                {searchValue && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Try adjusting your search criteria
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                {(() => {
                  // Group jobs by date range for plan mode
                  if (searchMode === "plan") {
                    const groupedJobs = {};
                    filteredJobs.forEach((item) => {
                      const groupLabel = item.label;
                      if (!groupedJobs[groupLabel]) {
                        groupedJobs[groupLabel] = [];
                      }
                      groupedJobs[groupLabel].push(item);
                    });

                    return Object.entries(groupedJobs).map(([groupLabel, jobs], groupIndex) => (
                      <Box key={`group-${groupIndex}`} sx={{ mb: 1 }}>
                        {/* Group Header */}
                        <Box
                          sx={{
                            p: 0.8,
                            mb: 0.5,
                            background: (t) =>
                              `linear-gradient(90deg, ${alpha(t.palette.primary.main, 0.12)}, ${alpha(
                                t.palette.primary.main,
                                0.04
                              )})`,
                            borderLeft: (t) => `3px solid ${t.palette.primary.main}`,
                            borderRadius: 1,
                            position: "sticky",
                            top: 0,
                            zIndex: 5,
                            backdropFilter: "blur(8px)"
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              color: (t) => t.palette.primary.main,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5
                            }}
                          >
                            <WorkIcon sx={{ fontSize: "0.9rem" }} />
                            {groupLabel}
                            <Chip
                              label={`${jobs.length} ${jobs.length === 1 ? "Job" : "Jobs"}`}
                              size="small"
                              sx={{
                                ml: 1,
                                height: 18,
                                fontSize: "0.65rem",
                                bgcolor: (t) => alpha(t.palette.primary.main, 0.15),
                                color: (t) => t.palette.primary.main,
                                fontWeight: 600
                              }}
                            />
                          </Typography>
                        </Box>

                        {/* Jobs in this group */}
                        {jobs.map((item, index) => {
                          const job = item.job;
                          return (
                            <Paper
                              key={`${job.jobNumber}-${index}`}
                              elevation={0}
                              onClick={() => {
                                setSelectedJobData(job);
                                setShowJobDetails(true);
                                handleDialogClose();
                              }}
                              sx={{
                                p: 0.8,
                                mb: 0.5,
                                borderRadius: 1,
                                border: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}`,
                                background: (t) => alpha(t.palette.background.paper, 0.8),
                                cursor: "pointer",
                                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                "&:hover": {
                                  transform: "translateX(4px)",
                                  backgroundColor: (t) => alpha(t.palette.primary.main, 0.08),
                                  borderColor: (t) => t.palette.primary.main,
                                  boxShadow: (t) => `0 2px 8px ${alpha(t.palette.primary.main, 0.15)}`
                                }
                              }}
                            >
                              {/* Single Row: Job# | Customer | Operation | Product | Chips */}
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  flexWrap: { xs: "wrap", sm: "nowrap" }
                                }}
                              >
                                {/* Job Number - Fixed width */}
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 700,
                                    color: (t) => t.palette.primary.main,
                                    fontSize: "1 rem",
                                    minWidth: "fit-content",
                                    flexShrink: 0
                                  }}
                                >
                                  {job.jobSequenceNumber ? `(${job.jobSequenceNumber}) ` : ""}
                                  {job.jobNumber}
                                </Typography>

                                {/* Divider */}
                                <Box
                                  sx={{
                                    width: "1px",
                                    height: "18px",
                                    bgcolor: (t) => alpha(t.palette.divider, 0.5),
                                    flexShrink: 0,
                                    display: { xs: "none", sm: "block" }
                                  }}
                                />

                                {/* Customer - Truncated */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.3,
                                    flex: { xs: "1 1 auto", sm: "0 1 20%" },
                                    minWidth: 0
                                  }}
                                  title={job.customer}
                                >
                                  <BusinessIcon
                                    sx={{
                                      fontSize: "0.9rem",
                                      color: (t) => t.palette.text.secondary,
                                      flexShrink: 0
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "0.75rem",
                                      fontWeight: 500,
                                      color: (t) => t.palette.text.primary,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap"
                                    }}
                                  >
                                    {job.customer}
                                  </Typography>
                                </Box>

                                {/* Divider */}
                                <Box
                                  sx={{
                                    width: "1px",
                                    height: "18px",
                                    bgcolor: (t) => alpha(t.palette.divider, 0.5),
                                    flexShrink: 0,
                                    display: { xs: "none", sm: "block" }
                                  }}
                                />

                                {/* Operation - Truncated */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.3,
                                    flex: { xs: "1 1 auto", sm: "0 1 20%" },
                                    minWidth: 0
                                  }}
                                  title={job.OpDesctiption}
                                >
                                  <PrecisionManufacturingIcon
                                    sx={{
                                      fontSize: "0.75rem",
                                      color: (t) => t.palette.text.secondary,
                                      flexShrink: 0
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "0.75rem",
                                      color: (t) => t.palette.text.primary,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap"
                                    }}
                                  >
                                    {job.OpDesctiption}
                                  </Typography>
                                </Box>

                                {/* Divider */}
                                <Box
                                  sx={{
                                    width: "1px",
                                    height: "18px",
                                    bgcolor: (t) => alpha(t.palette.divider, 0.5),
                                    flexShrink: 0,
                                    display: { xs: "none", sm: "block" }
                                  }}
                                />

                                {/* Product - Truncated */}
                                <Box
                                  sx={{
                                    flex: { xs: "1 1 100%", sm: "0 1 25%" },
                                    minWidth: 0
                                  }}
                                  title={job.WOdescription}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "0.85rem",
                                      color: (t) => t.palette.text.secondary,
                                      fontStyle: "italic",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap"
                                    }}
                                  >
                                    {job.WOdescription}
                                  </Typography>
                                </Box>

                                {/* Chips - Right aligned */}
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 0.4,
                                    ml: "auto",
                                    flexShrink: 0,
                                    flexWrap: "nowrap",
                                    alignItems: "center"
                                  }}
                                >
                                  {job.plannedQty && (
                                    <Chip
                                      label={job.plannedQty}
                                      size="small"
                                      sx={{
                                        bgcolor: (t) => alpha(t.palette.primary.main, 0.15),
                                        color: (t) => t.palette.primary.main,
                                        fontWeight: 600,
                                        fontSize: "0.65rem",
                                        height: 18,
                                        "& .MuiChip-label": { px: 0.6 }
                                      }}
                                    />
                                  )}
                                  {job.OperationalTime && (
                                    <Chip
                                      label={`${job.OperationalTime}h`}
                                      size="small"
                                      sx={{
                                        bgcolor: (t) => alpha(t.palette.info.main, 0.15),
                                        color: (t) => t.palette.info.main,
                                        fontWeight: 600,
                                        fontSize: "0.65rem",
                                        height: 18,
                                        "& .MuiChip-label": { px: 0.6 }
                                      }}
                                    />
                                  )}
                                  {job.SetupTime && (
                                    <Chip
                                      label={`S:${job.SetupTime}h`}
                                      size="small"
                                      sx={{
                                        bgcolor: (t) => alpha(t.palette.warning.main, 0.15),
                                        color: (t) => t.palette.warning.main,
                                        fontWeight: 600,
                                        fontSize: "0.65rem",
                                        height: 18,
                                        "& .MuiChip-label": { px: 0.6 }
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Paper>
                          );
                        })}
                      </Box>
                    ));
                  } else {

                    // Lineup mode WITH machineCode grouping
                    if (!searchValue.trim()) {
                      const groupedLineup = {};

                      filteredJobs.forEach((item) => {
                        const mc = item.job.machineCode || "UNKNOWN";
                        if (!groupedLineup[mc]) groupedLineup[mc] = [];
                        groupedLineup[mc].push(item);
                      });

                      return Object.entries(groupedLineup).map(([mc, jobs]) => (
                        <Box key={mc} sx={{ mb: 1.5 }}>
                          <Box
                            sx={{
                              p: 0.8,
                              mb: 0.5,
                              background: (t) =>
                                `linear-gradient(90deg, ${alpha(t.palette.primary.main, 0.12)}, ${alpha(
                                  t.palette.primary.main,
                                  0.04
                                )})`,
                              borderLeft: (t) => `3px solid ${t.palette.primary.main}`,
                              borderRadius: 1,
                              position: "sticky",
                              top: 0,
                              zIndex: 5,
                              backdropFilter: "blur(8px)",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.75rem",
                                color: (t) => t.palette.primary.main,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                              }}
                            >
                              {mc} — {jobs.length} {jobs.length === 1 ? "Job" : "Jobs"}
                            </Typography>
                          </Box>

                          {jobs.map((item, index) => {
                            const job = item.job;
                            return (
                              <Paper
                                key={`${job.jobNumber}-${index}`}
                                elevation={0}
                                onClick={() => {
                                  setSelectedJobData(job);
                                  setShowJobDetails(true);
                                  handleDialogClose();
                                }}
                                sx={{
                                  p: 0.8,
                                  mb: 0.5,
                                  borderRadius: 1,
                                  border: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}`,
                                  background: (t) => alpha(t.palette.background.paper, 0.8),
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  "&:hover": {
                                    transform: "translateX(4px)",
                                    backgroundColor: (t) => alpha(t.palette.primary.main, 0.08),
                                    borderColor: (t) => t.palette.primary.main,
                                  },
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Typography sx={{ fontWeight: 700, color: "primary.main", fontSize: "0.85rem" }}>
                                    {job.jobNumber}
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "0.75rem",
                                      fontWeight: 500,
                                      color: (t) => t.palette.text.primary,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {job.customer}
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "0.75rem",
                                      color: (t) => t.palette.text.secondary,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {job.OpDesctiption}
                                  </Typography>

                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "0.72rem",
                                      fontStyle: "italic",
                                      color: (t) => t.palette.text.secondary,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {job.WOdescription}
                                  </Typography>

                                  <Box sx={{ display: "flex", gap: 0.4, ml: "auto", flexShrink: 0 }}>
                                    {job.plannedQty && (
                                      <Chip
                                        label={job.plannedQty}
                                        size="small"
                                        sx={{
                                          bgcolor: (t) => alpha(t.palette.primary.main, 0.15),
                                          color: (t) => t.palette.primary.main,
                                          fontWeight: 600,
                                          fontSize: "0.65rem",
                                          height: 18,
                                        }}
                                      />
                                    )}

                                    {job.OperationalTime && (
                                      <Chip
                                        label={`${job.OperationalTime}h`}
                                        size="small"
                                        sx={{
                                          bgcolor: (t) => alpha(t.palette.info.main, 0.15),
                                          color: (t) => t.palette.info.main,
                                          fontWeight: 600,
                                          fontSize: "0.65rem",
                                          height: 18,
                                        }}
                                      />
                                    )}

                                    {job.SetupTime && (
                                      <Chip
                                        label={`S:${job.SetupTime}h`}
                                        size="small"
                                        sx={{
                                          bgcolor: (t) => alpha(t.palette.warning.main, 0.15),
                                          color: (t) => t.palette.warning.main,
                                          fontWeight: 600,
                                          fontSize: "0.65rem",
                                          height: 18,
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                              </Paper>
                            );
                          })}
                        </Box>
                      ));
                    } else {
                      // Searching → DO NOT group
                      return filteredJobs.map((item, index) => {
                        const job = item.job;
                        return (
                          <Paper
                            key={`${job.jobNumber}-${index}`}
                            elevation={0}
                            onClick={() => {
                              setSelectedJobData(job);
                              setShowJobDetails(true);
                              handleDialogClose();
                            }}
                            sx={{
                              p: 0.8,
                              mb: 0.5,
                              borderRadius: 1,
                              border: (t) => `1px solid ${alpha(t.palette.divider, 0.5)}`,
                              background: (t) => alpha(t.palette.background.paper, 0.8),
                              cursor: "pointer",
                              transition: "all 0.2s",
                              "&:hover": {
                                transform: "translateX(4px)",
                                backgroundColor: (t) => alpha(t.palette.primary.main, 0.08),
                                borderColor: (t) => t.palette.primary.main,
                              },
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography sx={{ fontWeight: 700, color: "primary.main", fontSize: "0.85rem" }}>
                                {job.jobNumber}
                              </Typography>
                            </Box>
                          </Paper>
                        );
                      });
                    }

                  }
                })()}
              </Box>
            )}
          </Box>
        </DialogContent>

      </Dialog>

      <Fade in timeout={600}>
        <StyledCard>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
              flexWrap: "wrap",
              gap: 1
            }}
          >
            <ShiftRemarksButton Employee_ID={username} />
            <HeaderSection>
              <TitleText variant="h5" component="h5">
                Job & Process Selection
              </TitleText>
            </HeaderSection>
            <ViewJobPlan username={username} />
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 1.5,
                borderRadius: 1.5,
                fontSize: "0.85rem",
                py: 0.5,
                border: (t) => `1px solid ${alpha(t.palette.error.main, 0.3)}`
              }}
            >
              {error}
            </Alert>
          )}

          {isLoading && (
            <Box sx={{ mb: 1.5 }}>
              <LinearProgress
                sx={{
                  height: 5,
                  borderRadius: 2,
                  backgroundColor: (t) => alpha(t.palette.primary.main, 0.12),
                  "& .MuiLinearProgress-bar": {
                    background: (t) =>
                      `linear-gradient(45deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
                    borderRadius: 2
                  }
                }}
              />
            </Box>
          )}

          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} md={selectedJobData ? 5 : 12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  background: (t) =>
                    `linear-gradient(135deg, ${alpha(t.palette.background.paper, 0.9)} 0%, ${alpha(
                      t.palette.background.default,
                      0.9
                    )} 100%)`,
                  border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.1)}`,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column"
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: (t) => t.palette.primary.main,
                      fontSize: "1.1rem"
                    }}
                  >
                    Search & Select Job
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}>
                  <ActionButton
                    variant="contained"
                    fullWidth
                    startIcon={<WorkIcon fontSize="small" />}
                    onClick={handleSearchFromPlan}
                    disabled={isLoading || !machineCode}
                    sx={{
                      background: (t) =>
                        `linear-gradient(45deg, ${t.palette.primary.main} 30%, ${t.palette.secondary.main} 90%)`,
                      fontSize: "0.9rem",
                      maxWidth: "320px",
                      m: "0 auto"
                    }}
                  >
                    Search from Plan
                  </ActionButton>

                  <ActionButton
                    variant="contained"
                    fullWidth
                    startIcon={<PrecisionManufacturingIcon fontSize="small" />}
                    onClick={handleSearchFromLineup}
                    disabled={isLoading || !machineCode}
                    sx={{
                      background: (t) =>
                        `linear-gradient(45deg, ${t.palette.success.main} 30%, ${t.palette.success.dark} 90%)`,
                      fontSize: "0.9rem",
                      maxWidth: "320px",
                      m: "0 auto"
                    }}
                  >
                    Search from Lineup
                  </ActionButton>

                  <ActionButton
                    variant="contained"
                    fullWidth
                    startIcon={<CreateIcon fontSize="small" />}
                    onClick={handleclicktoManual}
                    sx={{
                      background: (t) =>
                        `linear-gradient(45deg, ${t.palette.error.main} 30%, ${t.palette.error.dark} 90%)`,
                      fontSize: "0.9rem",
                      maxWidth: "320px",
                      m: "0 auto"
                    }}
                  >
                    Manual Job Entry
                  </ActionButton>
                </Box>

                {!selectedJobData && (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 2,
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.9rem" }}>
                      No job selected. Click one of the buttons above to search and select a job.
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mt: "auto", textAlign: "center" }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    Current Information
                  </Typography>
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
                    <StatsChip label={`Machine: ${machineCode || "Loading..."}`} icon={<PrecisionManufacturingIcon fontSize="small" />} />
                    {employeeName && <StatsChip label={`Operator: ${employeeName}`} icon={<PersonIcon fontSize="small" />} />}
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {selectedJobData && (
              <Grid item xs={12} md={7}>
                <SelectedJobCard>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        fontSize: "1rem",
                        color: "white"
                      }}
                    >
                      <WorkIcon fontSize="small" /> Selected Job Details
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Tooltip title={showJobDetails ? "Collapse Details" : "Expand Details"}>
                        <IconButton
                          onClick={() => setShowJobDetails(!showJobDetails)}
                          sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.15)",
                            color: "white",
                            transition: "all 0.3s ease",
                            borderRadius: 2,
                            p: 1,
                            minWidth: 36,
                            minHeight: 36,
                            "&:hover": {
                              backgroundColor: "rgba(255, 255, 255, 0.25)",
                              transform: "scale(1.05)"
                            }
                          }}
                        >
                          {showJobDetails ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Clear Selected Job">
                        <IconButton
                          onClick={() => {
                            setSelectedJobData(null);
                            setShowJobDetails(false);
                          }}
                          sx={{
                            backgroundColor: (t) => alpha(t.palette.error.main, 0.15),
                            color: (t) => alpha(t.palette.error.contrastText, 0.9),
                            transition: "all 0.3s ease",
                            borderRadius: 2,
                            p: 1,
                            minWidth: 36,
                            minHeight: 36,
                            "&:hover": {
                              backgroundColor: (t) => alpha(t.palette.error.main, 0.25),
                              transform: "scale(1.05)"
                            }
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: "white", fontSize: "1.1rem", mb: 1 }}>
                      {selectedJobData.jobNumber}
                    </Typography>
                    <Typography variant="body1" sx={{ color: "rgba(255,255,255,0.9)", fontSize: "0.95rem" }}>
                      {selectedJobData.customer}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2, display: showJobDetails ? "block" : "none" }}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ opacity: 0.8, color: "white" }}>
                          Operation
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.9rem", color: "white" }}>
                          {selectedJobData.OpDesctiption}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" sx={{ opacity: 0.8, color: "white" }}>
                          Product
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.9rem", color: "white" }}>
                          {selectedJobData.WOdescription}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 1 }}>
                          {selectedJobData.plannedQty && (
                            <Chip
                              label={`Planned Qty: ${selectedJobData.plannedQty}`}
                              size="small"
                              sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontSize: "0.75rem" }}
                            />
                          )}
                          {selectedJobData.OperationalTime && (
                            <Chip
                              label={`Op Time: ${selectedJobData.OperationalTime}h`}
                              size="small"
                              sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontSize: "0.75rem" }}
                            />
                          )}
                          {selectedJobData.SetupTime && (
                            <Chip
                              label={`Setup Time: ${selectedJobData.SetupTime}h`}
                              size="small"
                              sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white", fontSize: "0.75rem" }}
                            />
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>

                  <Box sx={{ mt: "auto", textAlign: "center" }}>
                    <Tooltip title={!selectedJobData ? "Please select a job first" : ""}>
                      <span>
                        <ActionButton
                          size="medium"
                          variant="contained"
                          fullWidth
                          startIcon={
                            isStartJobButtonDisabled ? (
                              <PlayArrowIcon fontSize="small" />
                            ) : (
                              <NotStartedOutlinedIcon fontSize="small" />
                            )
                          }
                          onClick={handleStartJob}
                          disabled={isStartJobButtonDisabled || !selectedJobData}
                          sx={{
                            background: "linear-gradient(45deg, rgba(255,255,255,0.9) 30%, rgba(255,255,255,1) 90%)",
                            color: (t) => t.palette.primary.main,
                            minHeight: 42,
                            fontSize: "0.9rem",
                            fontWeight: 700,
                            "&:hover": {
                              background: "linear-gradient(45deg, rgba(255,255,255,1) 30%, rgba(255,255,255,1) 90%)",
                              transform: "translateY(-2px)",
                              boxShadow: "0 6px 15px rgba(0,0,0,0.2)"
                            },
                            "&:disabled": {
                              background: (t) => alpha(t.palette.common.white, 0.5),
                              color: (t) => alpha(t.palette.primary.main, 0.5)
                            }
                          }}
                        >
                          {isStartJobButtonDisabled ? "Starting Job..." : "Start Selected Job"}
                        </ActionButton>
                      </span>
                    </Tooltip>
                  </Box>
                </SelectedJobCard>
              </Grid>
            )}
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5, flexWrap: "wrap", mt: 3 }}>
            <ActionButton
              onClick={Buttonclickmaincategories}
              startIcon={<ArrowBackIcon fontSize="small" />}
              variant="outlined"
              sx={{
                borderColor: (t) => t.palette.primary.main,
                color: (t) => t.palette.primary.main,
                fontSize: "0.9rem",
                "&:hover": {
                  borderColor: (t) => t.palette.primary.main,
                  backgroundColor: (t) => alpha(t.palette.primary.main, 0.1)
                }
              }}
            >
              Back to Main Categories
            </ActionButton>
          </Box>
        </StyledCard>
      </Fade>
    </MainContainer>
  );
}

export default JobProcessSelection;